import { observer } from 'mobx-react-lite'
import { CalendarDropdown, FullPageWrapper, Typography, SpinnerLoader, Button } from 'shared/ui'
import { formatSecondsToLargestUnit } from 'shared/lib/dayjs'
import { uiStore } from 'shared/store/uiStore'
import {
  OverallAnalyticsPageProvider,
  useOverallAnalyticsPageContext,
} from 'pages/analytics/pages/overall/context/overallAnalyticsPageContext'
import { AnalyticsHeader, AnalyticsEmptyState } from 'pages/analytics/ui'
import { AnalyticsPath } from 'pages/analytics/route/analyticsPath'
import { OverallAnalyticsCard } from './OverallAnalyticsCard/OverallAnalyticsCard'
import styles from './styles.module.scss'

const OverallAnalyticsPageContent = observer(() => {
  const {
    pageLoading,
    hasData,
    calendarFilterStore,
    getPerformanceMetricByKey,
    getMessageMetricByKey,
    getCallMetricByKey,
    getMessageChartByKey,
    getPerformanceChartByKey,
    getCallChartByKey,
  } = useOverallAnalyticsPageContext()

  const handleNavigateToPerformance = () => {
    uiStore.changeRoute({ path: AnalyticsPath.performance })
  }

  const handleNavigateToMessages = () => {
    uiStore.changeRoute({ path: AnalyticsPath.messages })
  }

  const handleNavigateToCalls = () => {
    uiStore.changeRoute({ path: AnalyticsPath.calls })
  }

  const formatPerformanceValue = (value: number) => {
    return value > 0 ? formatSecondsToLargestUnit(value) : '0s'
  }

  const renderContent = () => {
    if (pageLoading) {
      return (
        <div className={styles.fullPageContainer}>
          <SpinnerLoader ariaLabel={'OverallAnalyticsPage'} />
        </div>
      )
    }

    if (!hasData) {
      return (
        <div className={styles.fullPageContainer}>
          <AnalyticsEmptyState />
        </div>
      )
    }

    return (
      <div className={styles.sectionsContainer}>
        <div>
          <div className={styles.sectionHeader}>
            <Typography variant='heading-sm' ariaLabel='PerformanceSectionTitle'>
              Performance
            </Typography>
            <Button
              text='Details'
              contained='secondary'
              size='medium'
              onClick={handleNavigateToPerformance}
            />
          </div>
          <div className={styles.metricsRow} onClick={handleNavigateToPerformance}>
            <OverallAnalyticsCard
              title='Avg. Time to First Response'
              value={getPerformanceMetricByKey('avg_time_to_first_response')?.value}
              data={getPerformanceChartByKey('avg_time_to_first_response')}
              formatValue={formatPerformanceValue}
            />
            <OverallAnalyticsCard
              title='Avg. Time to Response'
              value={getPerformanceMetricByKey('avg_time_to_response')?.value}
              data={getPerformanceChartByKey('avg_time_to_response')}
              formatValue={formatPerformanceValue}
            />
            <OverallAnalyticsCard
              title='Avg. Time to Close'
              value={getPerformanceMetricByKey('avg_time_to_close')?.value}
              data={getPerformanceChartByKey('avg_time_to_close')}
              formatValue={formatPerformanceValue}
            />
          </div>
        </div>

        <div>
          <div className={styles.sectionHeader}>
            <Typography variant='heading-sm' ariaLabel='MessagesSectionTitle'>
              Messages
            </Typography>
            <Button
              text='Details'
              contained='secondary'
              size='medium'
              onClick={handleNavigateToMessages}
            />
          </div>
          <div className={styles.metricsRow} onClick={handleNavigateToMessages}>
            <OverallAnalyticsCard
              title='Total messages'
              value={getMessageMetricByKey('total')?.value}
              data={getMessageChartByKey('total')}
            />
            <OverallAnalyticsCard
              title='Inbound messages'
              value={getMessageMetricByKey('total_inbound')?.value}
              data={getMessageChartByKey('total_inbound')}
            />
            <OverallAnalyticsCard
              title='Outbound messages'
              value={getMessageMetricByKey('total_outbound')?.value}
              data={getMessageChartByKey('total_outbound')}
            />
          </div>
        </div>

        <div>
          <div className={styles.sectionHeader}>
            <Typography variant={'heading-sm'} ariaLabel='CallsSectionTitle'>
              Calls
            </Typography>
            <Button
              text='Details'
              contained='secondary'
              size='medium'
              onClick={handleNavigateToCalls}
            />
          </div>
          <div className={styles.metricsRow} onClick={handleNavigateToCalls}>
            <OverallAnalyticsCard
              title='Total calls'
              value={getCallMetricByKey('total')?.value}
              data={getCallChartByKey('total')}
            />
            <OverallAnalyticsCard
              title='Inbound calls'
              value={getCallMetricByKey('inbound_calls')?.value}
              data={getCallChartByKey('inbound_calls')}
            />
            <OverallAnalyticsCard
              title='Outbound calls'
              value={getCallMetricByKey('outbound_calls')?.value}
              data={getCallChartByKey('outbound_calls')}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <FullPageWrapper className={styles.container}>
      <AnalyticsHeader
        title='Overall'
        actions={[{ buttonComponent: <CalendarDropdown store={calendarFilterStore} /> }]}
      />

      {renderContent()}
    </FullPageWrapper>
  )
})

export const OverallAnalyticsPage = () => (
  <OverallAnalyticsPageProvider>
    <OverallAnalyticsPageContent />
  </OverallAnalyticsPageProvider>
)
